import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  LinearProgress,
  Button,
  ButtonGroup,
  Skeleton,
  useTheme,
  alpha,
  TextField,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { enUS } from 'date-fns/locale';
import {
  TrendingUp,
  TrendingDown,
  Message as MessageIcon,
  Group as GroupIcon,
  Forum as ForumIcon,
  SmartToy as BotIcon,
  Refresh as RefreshIcon,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  Timeline,
  Analytics,
  Speed,
  AccessTime as ClockIcon,
  CheckCircle as CheckCircleIcon,
  Users as UsersIcon,
} from '@mui/icons-material';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  Legend,
  RadialBarChart,
  RadialBar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { format } from 'date-fns';
import conversationService from '../services/conversationService';
import { formatInArgentina, nowInArgentina, isToday, isYesterday, formatDuration, toArgentinaTime, fromArgentinaTime } from '../utils/timezone';
import { zonedTimeToUtc } from 'date-fns-tz';

// Professional color palette inspired by Azure/GCP
const colors = {
  primary: '#0078D4',      // Azure blue
  secondary: '#40E0D0',    // Turquoise
  success: '#107C10',      // Green
  warning: '#FFB900',      // Yellow
  danger: '#D13438',       // Red
  purple: '#5C2D91',       // Purple
  background: '#F8F9FA',   // Light gray
  surface: '#FFFFFF',      // White
  text: {
    primary: '#323130',
    secondary: '#605E5C',
    disabled: '#A19F9D',
  },
  chart: [
    '#0078D4', // Azure Blue
    '#40E0D0', // Turquoise
    '#107C10', // Green
    '#FFB900', // Yellow
    '#5C2D91', // Purple
    '#D13438', // Red
  ]
};

// Custom styled components
const MetricCard = ({ title, value, secondaryValue, icon: Icon, color, loading, unit, secondaryUnit }) => {
  return (
    <Card
      sx={{
        height: '100%',
        background: 'white',
        border: 'none',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                background: alpha(color, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
              }}
            >
              <Icon sx={{ fontSize: 22, color }} />
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.text.secondary,
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {title}
            </Typography>
          </Box>
          
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
            {loading ? (
              <Skeleton variant="text" width={100} height={40} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: colors.text.primary,
                    fontWeight: 600,
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'baseline'
                  }}
                >
                  {value?.toLocaleString() || '0'}
                  {unit && <Typography variant="body1" component="span" sx={{ ml: 0.5, color: colors.text.secondary }}>{unit}</Typography>}
                </Typography>
                
                {secondaryValue !== undefined && (
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: colors.text.primary,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'baseline'
                    }}
                  >
                    {secondaryValue}
                    {secondaryUnit && <Typography variant="body1" sx={{ ml: 0.5, color: colors.text.secondary }}>{secondaryUnit}</Typography>}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ title, subtitle, children, action }) => {
  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        background: 'white',
        border: 'none',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.text.primary,
              fontWeight: 600,
              fontSize: '1.125rem',
              mb: 0.5
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.text.secondary,
                fontSize: '0.875rem'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box>{action}</Box>}
      </Box>
      {children}
    </Paper>
  );
};

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Prefer exact timestamp if present (so we can show HH:mm), fallback to provided label
    const ts = payload[0]?.payload?.timestamp;
    // If timestamp contains timezone info (ends with offset like -03:00), parse it directly
    const displayText = ts 
      ? (ts.includes('T') && (ts.includes('+') || ts.includes('-')) 
        ? format(new Date(ts), 'dd/MM/yyyy HH:mm') // Already in local time with TZ
        : formatInArgentina(new Date(ts), 'dd/MM/yyyy HH:mm')) // UTC timestamp
      : (payload[0]?.payload?.fullDate || label);

    return (
      <Box
        sx={{
          background: 'white',
          p: 1.5,
          borderRadius: 1,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Typography variant="caption" sx={{ color: colors.text.secondary, display: 'block', mb: 0.5 }}>
          {displayText}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color, fontWeight: 600 }}
          >
            {entry.name}: {entry.value.toLocaleString()}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

function ConversationsAnalytics() {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total_conversations_period: 0,
    unanswered_conversations: 0,
    median_messages_per_conversation: 0,
    unique_users: 0,
    bot_message_ratio: 0,
    answered_sessions: 0,
    unanswered_sessions: 0,
    answered_ratio: 0,
    median_conversation_duration_minutes: 0,
  });
  const [series, setSeries] = useState([]);
  const [countryRanking, setCountryRanking] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('1');
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [rankingMetric, setRankingMetric] = useState('sessions');
  const [dataUpdating, setDataUpdating] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [availableDateRange, setAvailableDateRange] = useState({ minDate: null, maxDate: null });

  // Combined effect for loading data when period or custom dates change
  useEffect(() => {
    // Load data when:
    // 1. timePeriod changes to non-custom value
    // 2. timePeriod is custom AND both dates are selected
    // 3. custom dates change when in custom mode
    if (timePeriod !== 'custom') {
      loadDashboardData();
    } else if (customDateRange.from && customDateRange.to) {
      // Small delay to avoid multiple calls when both dates change quickly
      const timeoutId = setTimeout(() => {
        loadDashboardData();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, customDateRange.from, customDateRange.to, rankingMetric]);

  // Auto-refresh data every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing dashboard data in background...');
      loadDashboardData(true); // Pass true for background refresh
    }, 15000); // 15 seconds

    return () => clearInterval(interval); // Cleanup on component unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, customDateRange.from, customDateRange.to, rankingMetric]); // Relaunch interval when parameters change

  const loadDashboardData = async (isBackgroundRefresh = false) => {
    try {
      console.log('🔄 Loading dashboard data...', { timePeriod, isBackgroundRefresh });
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      let dateFrom = null;
      let dateTo = null;

      if (timePeriod === 'custom') {
        if (customDateRange.from && customDateRange.to) {
          console.log('📅 Custom date range selected:', {
            from: customDateRange.from.toLocaleDateString('en-US'),
            to: customDateRange.to.toLocaleDateString('en-US'),
            fromDateTime: customDateRange.from.toString(),
            toDateTime: customDateRange.to.toString()
          });
          
          // Convert Buenos Aires dates to UTC for API
          // User selected dates are in local time (Buenos Aires)
          // We need to convert them to UTC for the backend
          
          // Start of day in Buenos Aires
          const fromLocal = new Date(customDateRange.from);
          fromLocal.setHours(0, 0, 0, 0);
          
          // End of day in Buenos Aires
          const toLocal = new Date(customDateRange.to);
          toLocal.setHours(23, 59, 59, 999);
          
          // Convert to UTC using fromArgentinaTime
          dateFrom = fromArgentinaTime(fromLocal).toISOString();
          dateTo = fromArgentinaTime(toLocal).toISOString();
          
          console.log('📅 Final date range for API:', {
            dateFrom,
            dateTo
          });
          
        } else {
          // Don't fetch data if custom range is incomplete
          setLoading(false);
          return;
        }
      } else {
        // Calculate date range in Buenos Aires time
        const days = parseFloat(timePeriod);

        if (timePeriod === '0.083') {
          // Last 2 hours from current time in Buenos Aires
          const endLocal = nowInArgentina();
          const startLocal = new Date(endLocal);
          startLocal.setHours(startLocal.getHours() - 2);

          // Convert to UTC for API
          dateFrom = fromArgentinaTime(startLocal).toISOString();
          dateTo = fromArgentinaTime(endLocal).toISOString();

          console.log('🕒 Last 2h window:', {
            from: formatInArgentina(startLocal, 'dd/MM/yyyy HH:mm'),
            to: formatInArgentina(endLocal, 'dd/MM/yyyy HH:mm'),
            dateFromUTC: dateFrom,
            dateToUTC: dateTo
          });
        } else if (timePeriod === '1') {
          // Last 24 hours from current time in Buenos Aires
          const endLocal = nowInArgentina();
          // Don't truncate - include up to current moment
          const startLocal = new Date(endLocal);
          startLocal.setHours(startLocal.getHours() - 24);

          // Convert to UTC for API
          dateFrom = fromArgentinaTime(startLocal).toISOString();
          dateTo = fromArgentinaTime(endLocal).toISOString();

          console.log('🕒 Last 24h window:', {
            from: formatInArgentina(startLocal, 'dd/MM/yyyy HH:mm'),
            to: formatInArgentina(endLocal, 'dd/MM/yyyy HH:mm'),
            dateFromUTC: dateFrom,
            dateToUTC: dateTo
          });
        } else {
          // For multi-day ranges, use an anchored window: [now - (days-1) days at 00:00, now at 23:59:59]
          const nowLocal = nowInArgentina();
          const endLocal = new Date(nowLocal);
          endLocal.setHours(23, 59, 59, 999);

          const startLocal = new Date(nowLocal);
          startLocal.setDate(startLocal.getDate() - (days - 1));
          startLocal.setHours(0, 0, 0, 0);

          dateFrom = fromArgentinaTime(startLocal).toISOString();
          dateTo = fromArgentinaTime(endLocal).toISOString();

          console.log(`📅 ${days} days anchored window:`, {
            from: startLocal.toLocaleDateString('en-US'),
            to: endLocal.toLocaleDateString('en-US'),
            dateFromUTC: dateFrom,
            dateToUTC: dateTo
          });
        }
      }

      const bucket = timePeriod === '0.083' ? '10m' : timePeriod === '1' ? '30m' : '1h';
      const tz = 'America/Argentina/Buenos_Aires';
      console.log('📊 Fetching data with params:', { 
        dateFrom, 
        dateTo,
        timePeriod,
        bucket,
        tz,
        customRange: timePeriod === 'custom' ? customDateRange : null 
      });

      // Fetch all data in parallel
      const [statsData, seriesData, rankingData, heatmapRawData] = await Promise.all([
        conversationService.getMetrics({
          date_from: (timePeriod === '7' || timePeriod === '30' || timePeriod === '90' || timePeriod === 'custom') ? formatInArgentina(new Date(dateFrom), 'yyyy-MM-dd') : undefined,
          date_to: (timePeriod === '7' || timePeriod === '30' || timePeriod === '90' || timePeriod === 'custom') ? formatInArgentina(new Date(dateTo), 'yyyy-MM-dd') : undefined,
          window_hours: (timePeriod === '0.083') ? 2 : (timePeriod === '1') ? 24 : undefined,
          tz
        }),
        conversationService.getTimeseries({ 
          date_from: dateFrom, 
          date_to: dateTo, 
          bucket, 
          tz, 
          window_hours: timePeriod === '0.083' ? 2 : timePeriod === '1' ? 24 : undefined 
        }),
        conversationService.getTopCountries({ date_from: dateFrom, date_to: dateTo }),
        conversationService.getActivityHeatmap({ date_from: dateFrom, date_to: dateTo, tz })
      ]);
      
      console.log('✅ Data received:', {
        stats: statsData,
        series: seriesData?.length || 0,
        ranking: rankingData?.length || 0,
        heatmap: heatmapRawData?.length || 0
      });
      
      // Show updating animation
      setDataUpdating(true);
      
      // Update all data - the refresh should update everything
      setStats(statsData);
      
      // Update available date range for datepicker limits
      if (statsData.available_date_range) {
        setAvailableDateRange({
          minDate: statsData.available_date_range.min_date,
          maxDate: statsData.available_date_range.max_date
        });
        console.log('📅 Available date range updated:', {
          min: new Date(statsData.available_date_range.min_date).toLocaleDateString('en-US'),
          max: new Date(statsData.available_date_range.max_date).toLocaleDateString('en-US')
        });
      }
      formatAndSetSeries(seriesData);
      
      // Map backend fields to frontend expected fields
      const mappedRanking = rankingData.map(country => ({
        ...country,
        flag: country.flag_emoji,
        country: country.country_name
      }));
      setCountryRanking(mappedRanking);
      
      // Process heatmap data
      setHeatmapData(heatmapRawData || []);
      
      // Remove updating animation after a short delay
      setTimeout(() => setDataUpdating(false), 500);

      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  };
  
  const formatAndSetSeries = (data) => {
      console.log('📈 Formatting series data:', data);
      if (data && Array.isArray(data)) {
        // Prefer backend-provided local timestamp if available
        const formattedData = data.map((item, index) => {
          // Map backend fields to frontend expectations
          const sessions = item.sessions || item.count || 0;
          const unanswered = item.unanswered_sessions || 0;
          const answered = item.answered_sessions || 0;

          // Robust date parsing
          let itemDate;
          try {
            const sourceTs = item.time_local || item.time || item.date;
            itemDate = new Date(sourceTs);
            if (isNaN(itemDate.getTime())) {
              throw new Error('Invalid date created');
            }
          } catch (error) {
            console.error(`Invalid date format for item:`, item);
            // Fallback to now or skip item to avoid crashing
            itemDate = new Date(); 
          }

          if (timePeriod === '0.083' || timePeriod === '1') {
            // For hourly views - convert UTC to Buenos Aires time for display
            const hour = formatInArgentina(itemDate, 'HH:mm');
            const dayLabel = formatInArgentina(itemDate, 'dd/MM/yyyy');
            
            // Check if this is the first item or if day changed in Buenos Aires time
            const argDate = toArgentinaTime(itemDate);
            const prevArgDate = index > 0 ? toArgentinaTime(new Date(data[index - 1]?.time || data[index - 1]?.date)) : null;
            const isNewDay = index === 0 || (prevArgDate && argDate.getDate() !== prevArgDate.getDate());
            
            // Add day indicator when crossing midnight or for first item
            let displayLabel = hour;
            if (isNewDay) {
              // Show date in dd/MM format for clarity
              displayLabel = `${hour} - ${formatInArgentina(itemDate, 'dd/MM')}`;
            }
            
            const isPartial = index === data.length - 1; // last bucket may be partial
            return {
              date: displayLabel,  // Show hour with day indicator when needed
              fullDate: `${dayLabel} ${hour}`,  // Keep full date for tooltips
              timestamp: item.time_local || item.time || item.date,
              sessions: sessions,
              unanswered: unanswered,
              answered: answered,
              messages: item.messages || 0,
              bot: item.bot || 0,
              user: item.user || 0,
              isNewDay: isNewDay,  // Mark day boundaries
              isPartial
            };
          } else {
            // For daily views over a longer period - convert to Buenos Aires time
            return {
              date: formatInArgentina(itemDate, 'dd/MM'),
              timestamp: item.time_local || item.time || item.date,
              sessions: sessions,
              unanswered: unanswered,
              answered: answered,
              messages: item.messages || item.message_count || 0,
              bot: item.bot || 0,
              user: item.user || 0,
            };
          }
        });
        setSeries(formattedData);
      } else {
        setSeries([]);
      }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  // Calculate percentage changes (mock data for now)
  const calculateChange = () => {
    return Math.floor(Math.random() * 40) - 10; // Random between -10 and 30
  };

  // Prepare pie chart data - THIS WILL BE REPLACED
  const pieData = stats ? [
    { name: 'User Messages', value: stats.total_messages - stats.bot_messages, color: colors.chart[0] },
    { name: 'Bot Messages', value: stats.bot_messages, color: colors.chart[1] },
  ] : [];

  const maxCountrySessions = countryRanking.reduce((max, c) => Math.max(max, c.sessions || 0), 0);

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-auto h-[calc(100vh-64px)]">
      <div className="flex-1 p-4 overflow-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-1">
                Conversations Analytics
              </h1>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <CalendarToday sx={{ fontSize: 16 }} />
                Last updated: {lastRefresh.toLocaleDateString('en-US')} {format(lastRefresh, 'HH:mm:ss')}
                <IconButton 
                  onClick={handleRefresh} 
                  disabled={refreshing} 
                  size="small" 
                  sx={{ ml: 0.5, p: 0.5 }}
                >
                  <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </p>
            </div>
            
            <div className="flex gap-2 items-center">
              <ButtonGroup size="small" sx={{ bgcolor: 'white', borderRadius: 1 }}>
                <Button variant={timePeriod === '0.083' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('0.083')}>2 Hours</Button>
                <Button variant={timePeriod === '1' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('1')}>24 Hours</Button>
                <Button variant={timePeriod === '7' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('7')}>7 Days</Button>
                <Button variant={timePeriod === '30' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('30')}>30 Days</Button>
                <Button variant={timePeriod === '90' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('90')}>90 Days</Button>
                <Button variant={timePeriod === 'custom' ? 'contained' : 'outlined'} onClick={() => setTimePeriod('custom')}>Custom</Button>
              </ButtonGroup>
              
              {/* Custom Date Range Pickers - MUI DatePickers */}
              {timePeriod === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {availableDateRange.minDate && availableDateRange.maxDate && (
                      <MuiTooltip 
                        title={`Data available from ${new Date(availableDateRange.minDate).toLocaleDateString('en-US')} to ${new Date(availableDateRange.maxDate).toLocaleDateString('en-US')}`}
                        arrow
                      >
                        <span style={{ fontSize: '1.2rem', cursor: 'help', color: '#1976d2' }}>ℹ️</span>
                      </MuiTooltip>
                    )}
                    <DatePicker
                      label="From"
                      value={customDateRange.from}
                      onChange={(newValue) => {
                        if (newValue) {
                          // Create date at noon to avoid DST issues
                          const date = new Date(newValue);
                          date.setHours(12, 0, 0, 0);
                          console.log('📅 From date selected:', date.toLocaleDateString('en-US'));
                          setCustomDateRange(prev => ({ ...prev, from: date }));
                        } else {
                          setCustomDateRange(prev => ({ ...prev, from: null }));
                        }
                      }}
                      format="dd/MM/yyyy"
                      minDate={availableDateRange.minDate ? new Date(availableDateRange.minDate) : undefined}
                      maxDate={availableDateRange.maxDate ? new Date(availableDateRange.maxDate) : new Date()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: 160 }
                        }
                      }}
                    />
                    <DatePicker
                      label="To"
                      value={customDateRange.to}
                      onChange={(newValue) => {
                        if (newValue) {
                          // Create date at noon to avoid DST issues
                          const date = new Date(newValue);
                          date.setHours(12, 0, 0, 0);
                          console.log('📅 To date selected:', date.toLocaleDateString('en-US'));
                          setCustomDateRange(prev => ({ ...prev, to: date }));
                        } else {
                          setCustomDateRange(prev => ({ ...prev, to: null }));
                        }
                      }}
                      format="dd/MM/yyyy"
                      minDate={customDateRange.from || (availableDateRange.minDate ? new Date(availableDateRange.minDate) : undefined)}
                      maxDate={availableDateRange.maxDate ? new Date(availableDateRange.maxDate) : new Date()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: 160 }
                        }
                      }}
                    />
                    {(!customDateRange.from || !customDateRange.to) && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Select both dates
                      </Typography>
                    )}
                  </Box>
                </LocalizationProvider>
              )}
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* CONVERSATIONS / ANSWERED / UNANSWERED */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Conversations (Answered / Unanswered)</p>
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-full">
                <ForumIcon className="h-5 w-5 text-blue-500" />
              </div>
              <p className={`text-3xl font-bold text-gray-800 ml-3 transition-all duration-300 ${dataUpdating ? 'opacity-50' : 'opacity-100'}`}>
                {(stats?.total_conversations || 0).toLocaleString()}
                <span className="text-xl font-semibold ml-2">
                  (<span style={{ color: colors.primary }}>{(stats?.answered_conversations || 0).toLocaleString()}</span>/<span style={{ color: colors.danger }}>{(stats?.unanswered_conversations || 0).toLocaleString()}</span>)
                </span>
              </p>
            </div>
          </div>

          {/* MEDIAN INTERACTION */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Response Time Median</p>
            <div className="flex items-center">
              <div className="bg-green-100 p-2 rounded-full">
                <ClockIcon className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <p className={`text-3xl font-bold text-gray-800 transition-all duration-300 ${dataUpdating ? 'opacity-50' : 'opacity-100'}`}>
                  {formatDuration((stats?.median_interaction_seconds || 0) / 60)}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">50% resolved within this time</p>
              </div>
            </div>
          </div>

          {/* ANSWERED RATIO */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Answered Ratio</p>
            <div className="flex items-center">
              <div className="bg-teal-100 p-2 rounded-full">
                <CheckCircleIcon className="h-5 w-5 text-teal-500" />
              </div>
              <p className={`text-3xl font-bold text-gray-800 ml-3 transition-all duration-300 ${dataUpdating ? 'opacity-50' : 'opacity-100'}`}>{(stats?.answered_ratio || 0).toFixed(2)}%</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="space-y-4">
          {/* Row 1: Activity Chart and Top Countries */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
                      <ChartCard
            title="Activity Overview"
                          subtitle={
              timePeriod === 'custom'
                ? customDateRange.from && customDateRange.to
                  ? `Sessions started on ${customDateRange.from.toLocaleDateString('en-US')}${
                      customDateRange.from.toDateString() !== customDateRange.to.toDateString() 
                        ? ` to ${customDateRange.to.toLocaleDateString('en-US')}` 
                        : ''
                    }`
                  : 'Select date range'
                : timePeriod === '0.083'
                  ? `10-minute intervals (last 2 hours from now - Buenos Aires time)`
                  : timePeriod === '1'
                  ? `30-minute intervals (last 24 hours from now - Buenos Aires time)`
                  : `Sessions started in the last ${timePeriod} days`
            }
            >
              {loading ? (
                <Box sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : series && series.length > 0 ? (
                <ResponsiveContainer width="100%" height={450}>
                    <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: colors.text.secondary, fontSize: 11 }}
                      angle={(timePeriod === '0.083' || timePeriod === '1') ? -20 : -45}
                      textAnchor={(timePeriod === '0.083' || timePeriod === '1') ? "end" : "end"}
                      height={(timePeriod === '0.083' || timePeriod === '1') ? 60 : 80}
                      interval={timePeriod === '0.083' ? 0 : timePeriod === '1' ? (index) => {
                        // Show every 2 hours (4 intervals of 30 min), but always show day changes
                        const item = series[index];
                        if (!item) return false;
                        return index % 4 === 0 || item.isNewDay;
                      } : 'preserveStartEnd'}
                    />
                    <YAxis 
                      tick={{ fill: colors.text.secondary, fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/* Visual hint for partial last bucket */}
                    {/* We keep area styles, but add a vertical reference line if last bucket is partial */}
                    <Legend verticalAlign="top" height={36}/>
                    <Area
                      type="monotone"
                      dataKey="answered"
                      stackId="1"
                      stroke={colors.primary}
                      fill={alpha(colors.primary, 0.2)}
                      name="Answered"
                    />
                    <Area
                      type="monotone"
                      dataKey="unanswered"
                      stackId="1"
                      stroke={colors.danger}
                      fill={alpha(colors.danger, 0.2)}
                      name="Unanswered"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No data available for this period.</Typography>
                </Box>
              )}
            </ChartCard>
          </div>

          {/* Country Ranking */}
          <div className="lg:col-span-1">
            <ChartCard
              title={`Top 10 Countries by Conversations`}
              subtitle={`Based on conversations in the selected period`}
            >
              <Box sx={{ pr: 1 }}>
                {loading ? (
                  Array.from(new Array(8)).map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" height={20} />
                        <Skeleton variant="text" height={12} width="80%" />
                      </Box>
                    </Box>
                  ))
                ) : countryRanking.length > 0 ? (
                  countryRanking.map((item, index) => {
                    const sessions = item.total_sessions || 0;
                    const answered = item.answered_sessions || 0;
                    const unanswered = item.unanswered_sessions || 0;
                    
                    // Calculate percentages for the progress bar
                    const answeredPercent = sessions > 0 ? (answered / sessions) * 100 : 0;
                    const unansweredPercent = sessions > 0 ? (unanswered / sessions) * 100 : 0;

                    return (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography 
                          component="span"
                          sx={{ 
                            mr: 1.5, 
                            fontSize: '1.5rem',
                            fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          {item.flag || '🏳️'}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text.primary }}>
                              {item.country}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary }}>
                              {sessions.toLocaleString()}
                            </Typography>
                          </Box>
                          <MuiTooltip title={`Answered: ${answered.toLocaleString()} (${answeredPercent.toFixed(1)}%) • Unanswered: ${unanswered.toLocaleString()} (${unansweredPercent.toFixed(1)}%)`} placement="top" arrow>
                            <Box
                              sx={{
                                position: 'relative',
                                height: 12,
                                borderRadius: 6,
                                bgcolor: '#e0e0e0', // Gray background for total
                                overflow: 'hidden',
                                border: '1px solid rgba(0,0,0,0.1)',
                              }}
                            >
                              {/* Answered segment (blue) */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: `${answeredPercent}%`,
                                  bgcolor: colors.primary,
                                  transition: 'width 0.3s ease-in-out',
                                  minWidth: answeredPercent > 0 ? '2px' : 0, // Ensure visibility
                                }}
                              />
                              {/* Unanswered segment (red) - explicitly shown */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: `${answeredPercent}%`,
                                  top: 0,
                                  bottom: 0,
                                  width: `${unansweredPercent}%`,
                                  bgcolor: alpha(colors.danger, 0.3),
                                  transition: 'all 0.3s ease-in-out',
                                }}
                              />
                            </Box>
                          </MuiTooltip>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No participant data to rank countries.</Typography>
                  </Box>
                )}
              </Box>
            </ChartCard>
          </div>
          </div>

          {/* Row 2: Conversation Types and Weekly Heat Map */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Conversation Types Radar Chart */}
            <ChartCard 
              title="Conversation Types"
              subtitle="Distribution of conversation categories"
            >
              <Box sx={{ height: 350, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={[
                      { category: 'Queries', value: 65, fullMark: 100 },
                      { category: 'Tasks', value: 45, fullMark: 100 },
                      { category: 'Discussions', value: 72, fullMark: 100 },
                      { category: 'Announcements', value: 38, fullMark: 100 },
                      { category: 'Materials', value: 55, fullMark: 100 },
                      { category: 'Evaluations', value: 28, fullMark: 100 },
                    ]}>
                      <PolarGrid stroke={colors.border} strokeDasharray="3 3" />
                      <PolarAngleAxis 
                        dataKey="category" 
                        tick={{ fill: colors.text.secondary, fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: colors.text.secondary, fontSize: 10 }}
                      />
                      <Radar 
                        name="Conversation Types" 
                        dataKey="value" 
                        stroke={colors.primary} 
                        fill={colors.primary} 
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </ChartCard>

            {/* Weekly Heat Map */}
            <ChartCard 
              title="Weekly Heat Map"
              subtitle="Session activity patterns by day and hour"
            >
              <Box sx={{ height: 350, p: 2 }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Box>
                    {/* Heat Map Grid */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Day labels */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 50, pt: 3 }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                          <Box
                            key={day}
                            sx={{
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              pr: 1,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                              {day}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Heat Map Cells */}
                      <Box sx={{ flex: 1 }}>
                        {/* Hour labels */}
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                          {['8am', '10am', '12pm', '2pm', '4pm', '6pm', '8pm', '10pm'].map((hour) => (
                            <Box
                              key={hour}
                              sx={{
                                flex: 1,
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: colors.text.secondary, fontSize: '10px' }}>
                                {hour}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Heat Map Grid */}
                        {(() => {
                          // Create a lookup map from heatmap data
                          // Backend: day_of_week (0=Sun, 1=Mon, ..., 6=Sat)
                          // Frontend: dayIndex (0=Mon, 1=Tue, ..., 6=Sun)
                          const heatmapMap = {};
                          heatmapData.forEach(item => {
                            const key = `${item.day_of_week}-${item.hour}`;
                            heatmapMap[key] = item.total_sessions || 0;
                          });
                          
                          // Find max sessions for normalization
                          const maxSessions = Math.max(...Object.values(heatmapMap), 1);
                          
                          return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIndex) => {
                            // Map frontend dayIndex to backend day_of_week
                            // Frontend: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
                            // Backend:  Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
                            const backendDayOfWeek = dayIndex === 6 ? 0 : dayIndex + 1;
                            
                            return (
                              <Box key={day} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                {[8, 10, 12, 14, 16, 18, 20, 22].map((hour, hourIndex) => {
                                  // Aggregate sessions for 2-hour block (e.g., 8-10am = hour 8 and 9)
                                  const sessions1 = heatmapMap[`${backendDayOfWeek}-${hour}`] || 0;
                                  const sessions2 = heatmapMap[`${backendDayOfWeek}-${hour + 1}`] || 0;
                                  const totalSessions = sessions1 + sessions2;
                                  const opacity = maxSessions > 0 ? totalSessions / maxSessions : 0;
                                  
                                  return (
                                    <MuiTooltip
                                      key={`${dayIndex}-${hourIndex}`}
                                      title={`${totalSessions} sessions`}
                                      arrow
                                      placement="top"
                                    >
                                      <Box
                                        sx={{
                                          flex: 1,
                                          height: 32,
                                          borderRadius: 0.5,
                                          bgcolor: alpha(colors.primary, opacity * 0.8 + 0.1),
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          border: '1px solid',
                                          borderColor: alpha(colors.primary, 0.1),
                                          '&:hover': {
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                          },
                                        }}
                                      />
                                    </MuiTooltip>
                                  );
                                })}
                              </Box>
                            );
                          });
                        })()}

                        {/* Legend */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            Less
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {[0.1, 0.3, 0.5, 0.7, 0.9].map((opacity) => (
                              <Box
                                key={opacity}
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 0.5,
                                  bgcolor: alpha(colors.primary, opacity),
                                  border: '1px solid',
                                  borderColor: alpha(colors.primary, 0.2),
                                }}
                              />
                            ))}
                          </Box>
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            More
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </ChartCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationsAnalytics;
